import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostBinding,
    inject,
    input,
    model,
    signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

/**
 * Collapsible section card matching the Figma form-section panel.
 * Renders a header (title + chevron) and projects its body. Stateless apart
 * from the two-way `collapsed` UI flag.
 *
 * When this is the only direct `lib-anc-form-section` inside `.anc-form`, the
 * header and chevron are hidden automatically (side-panel title carries the label).
 * Pass `[collapsible]="true"` to force the accordion header when there is one section.
 *
 * @example
 * <lib-anc-form-section [title]="'Sender Details'">
 *   ...fields...
 * </lib-anc-form-section>
 */
@Component({
    selector: 'lib-anc-form-section',
    imports: [CommonModule, TranslateModule],
    templateUrl: './anc-form-section.component.html',
    styleUrls: ['./anc-form-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AncFormSectionComponent implements AfterViewInit {
    /** Section heading (run through the translate pipe). */
    title = input<string>('');
    /** Section subtitle (run through the translate pipe). */
    subtitle = input<string>('');
    /** Two-way collapsed state; header click toggles it. */
    collapsed = model<boolean>(false);
    /**
     * Accordion header visibility. When omitted, hidden automatically for the
     * only section in a parent `.anc-form`.
     */
    collapsible = input<boolean | undefined>(undefined);

    /** Whether the accordion header (title + chevron) is shown. */
    showHeader = signal(true);

    private readonly elementRef = inject(ElementRef<HTMLElement>);
    private readonly cdr = inject(ChangeDetectorRef);

    @HostBinding('class.anc-section--static')
    get isStaticSection(): boolean {
        return !this.showHeader();
    }

    ngAfterViewInit(): void {
        const collapsible = this.collapsible();
        if (collapsible !== undefined) {
            this.showHeader.set(collapsible);
        } else {
            const parent = this.elementRef.nativeElement.parentElement;
            if (parent?.classList.contains('anc-form')) {
                const sectionCount = parent.querySelectorAll(
                    ':scope > lib-anc-form-section'
                ).length;
                this.showHeader.set(sectionCount > 1);
            }
        }
        this.cdr.markForCheck();
    }

    toggle(): void {
        if (!this.showHeader()) {
            return;
        }
        this.collapsed.update((v) => !v);
    }
}
